import { useState } from 'react'

import { FormValuesProps } from '../interfaces/form'
import HomeForm from '../form/home'
import AppBar from '../views/AppBar'
import AppFooter from '../views/AppFooter'

import * as _ from 'lodash'
import { bookAppointment, sendTeamsAppointment } from '../helper'
import { Typography, Container, Box, LinearProgress } from '@mui/material'
import { useTheme, styled, lighten } from '@mui/material/styles'

import QuoteBackground from '../assets/quote_background.png'
import { getReadableDate, isDaylightSavingTimeUK, trackEvent, trackGAEvent } from '../util'

export interface ThemeData {
  apiKey: string
  id: string
  themeId: string
  version: string
  email: string
  logo: string
  emailCustomization: any
  appointmentCustomization: any
  links: any
}

const HomePage = () => {
  const theme = useTheme()
  //@ts-ignore
  const custom = theme?.custom
  const [formValues, setFormValues] = useState<FormValuesProps>({
    otherMonthlyExpenses: 0,
    currentPage: 0,
    totalPages: 0,
  })
  const [isLoading, setIsLoading] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)

  const CustomLinearProgress = styled(LinearProgress)({
    backgroundColor: lighten(custom?.customization?.primaryColor, 0.5),
    '& .MuiLinearProgress-bar': {
      backgroundColor: custom?.customization?.primaryColor,
    },
  })

  const handleSubmit = async (values: FormValuesProps) => {
    try {
      setIsLoading(true)
      setIsSuccess(false)
      setIsSubmitted(false)

      const {
        firstName,
        lastName,
        isVideoCall,
        appointmentDate,
        appointmentTime,
        email,
        isTelephoneCall,
      } = values
      const name = `${firstName} ${lastName}`
      const time = appointmentTime!.split(':')
      const startTime = `${appointmentDate}T${appointmentTime}:00+0${
        isDaylightSavingTimeUK() ? 1 : 0
      }00`
      const endTime = `${appointmentDate}T${
        appointmentTime?.split(':')[0]
      }:30:00+0${isDaylightSavingTimeUK() ? 1 : 0}00`
      const formData = Object.keys(values)
        .map((key) => {
          const data = values as any
          if (['currentPage', 'totalPages'].includes(key)) return null

          if (
            custom?.appointmentCustomization?.disableFaceToFaceMeeting &&
            key === 'isMeeting'
          )
            return null
          if (
            custom?.appointmentCustomization?.disableVideoCall &&
            key === 'isVideoCall'
          )
            return null
          if (
            custom?.appointmentCustomization?.disableTelephoneCall &&
            key === 'isTelephoneCall'
          )
            return null

          return {
            label: _.startCase(key),
            value: `${
              key === 'occupation' || key === 'partnerOccupation'
                ? data[key].description
                : data[key]
            }`,
          }
        })
        .filter(Boolean)
      const appointmentType =
        isVideoCall === 'yes'
          ? 'Video Call'
          : isTelephoneCall === 'yes'
          ? 'Telephone Call'
          : 'Face to Face Meeting'

      if (isVideoCall === 'yes') {
        //temsAppointmenthere
        await sendTeamsAppointment({
          apiKey: custom?.apiKey,
          name,
          email,
          startTime,
          endTime,
          props: {
            startTime: getReadableDate(startTime),
            endTime: getReadableDate(endTime),
            email,
            name,
            formData,
            companyLogo: custom?.logo,
            companyName: custom?.customization?.companyName || 'Meet Parker',
            theme: theme?.palette.primary.main,
            botName: custom?.customization?.botName,
            leadSourceType: 'video-appointment',
          },
          templateName:
            custom?.version === 'rightmove'
              ? 'call-appointment'
              : custom?.emailCustomization?.appointmentTemplate
              ? custom?.emailCustomization?.appointmentTemplate
              : 'video-appointment',
          emailCustomization: {
            ...custom?.emailCustomization,
            subject:
              custom?.emailCustomization?.subject || 'Mortgage Appointment',
          },
          appointmentCustomization: custom?.appointmentCustomization,
          admin: custom?.email,
          links: custom?.links,
        })
      } else {
        const defaultTemplate =
          values?.isTelephoneCall === 'yes'
            ? 'call-appointment'
            : 'face-to-face-appointment'
        await bookAppointment({
          apiKey: custom?.apiKey,
          name,
          email,
          props: {
            startTime: getReadableDate(startTime),
            endTime: getReadableDate(endTime),
            email,
            name,
            formData,
            companyLogo: custom?.logo,
            companyName: custom?.customization?.companyName || 'Meet Parker',
            theme: theme?.palette.primary.main,
            botName: custom?.customization?.botName,
            leadSourceType: defaultTemplate,
          },
          templateName: custom?.emailCustomization?.appointmentTemplate
            ? custom?.emailCustomization?.appointmentTemplate
            : defaultTemplate,
          emailCustomization: {
            ...custom?.emailCustomization,
            subject:
              custom?.emailCustomization?.subject || 'Mortgage Appointment',
          },
          appointmentCustomization: custom?.appointmentCustomization,
          admin: custom?.email,
          links: custom?.links,
        })
      }

      await trackEvent(custom?.apiKey!, {
        appointment: {
          type: appointmentType,
          date: startTime,
        },
        lead: values,
      })

      await trackGAEvent(custom?.googleAnalyticsTrackingId, {
        category: "MeetParkerChat",
        action: "Book Appointment",
      })

      setIsSuccess(true)
      setIsSubmitted(true)
      setIsLoading(false)
    } catch (err) {
      setIsLoading(false)
      setIsSuccess(false)
      setIsSubmitted(true)
    }

    return values
  }

  const renderPage = () => (
    <div
      id="page"
      style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}
    >
      {custom?.appConfig?.hideAppBar &&
      custom?.appConfig?.hideAppBar === 'yes' ? null : (
        <AppBar />
      )}
      <div
        style={{
          flex: '1',
          ...(formValues?.currentPage === formValues?.totalPages! - 2 && {
            backgroundImage: `url("${QuoteBackground}")`,
            backgroundPosition: 'bottom',
            backgroundSize: '100% auto',
            backgroundRepeat: 'no-repeat',
          }),
        }}
      >
        {custom?.appConfig?.hideProgressBar &&
        custom?.appConfig?.hideProgressBar === 'yes' ? null : (
          <CustomLinearProgress
            variant="determinate"
            color="primary"
            value={
              ((formValues?.currentPage! + 1) / formValues?.totalPages!) * 100
            }
          />
        )}
        <HomeForm
          formValues={formValues}
          setFormValues={setFormValues}
          initialValues={formValues}
          handleSubmit={handleSubmit}
        />
        {isSubmitted && (
          <Container maxWidth="md">
            <Box sx={{ mt: 7, mb: 12 }} width="100%">
              <Box
                margin="auto"
                padding="10px"
                sx={{
                  '@media (min-width:768px)': {
                    width: '50%',
                  },
                  '@media (max-width:767px)': {
                    width: '80%',
                  },
                  background: isSuccess ? '#28a745' : '#dc3545',
                  borderRadius: '4px',
                }}
              >
                <Typography fontSize={16} color="white">
                  {isSuccess
                    ? `You're all booked in - You will receive an email confirmation shortly.`
                    : `An error has occured, please try again`}
                </Typography>
              </Box>
            </Box>
          </Container>
        )}
        {isLoading && (
          <Container maxWidth="md">
            <Box sx={{ mt: 7, mb: 12 }} width="100%">
              <Box
                margin="auto"
                padding="10px"
                sx={{
                  '@media (min-width:768px)': {
                    width: '50%',
                  },
                  '@media (max-width:767px)': {
                    width: '80%',
                  },
                  background: '#0dcaf0',
                  borderRadius: '4px',
                }}
              >
                <Typography fontSize={16} color="white">
                  Please wait while we're booking your appointment...
                </Typography>
              </Box>
            </Box>
          </Container>
        )}
      </div>
      {custom?.appConfig?.hideFooter &&
      custom?.appConfig?.hideFooter === 'yes' ? null : (
        <AppFooter />
      )}
    </div>
  )

  return renderPage()
}

export default HomePage
